import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomTable } from '../components/common/CustomTable';
import { searchEventsRequest } from '../api/requests/events';
import { EventType, EventSearchParams } from '../types/types';
import { EventStatus } from '../enums/enums';
import { EVENTCREATIONPATH, EVENTMANAGEMENTPATH } from '../routes/routePaths';
import { convertUtcToLocal } from '../utils/dateUtils';
import { DateFormatStrings, TEXT_SIZES } from '../utils/constants';
import { StyledSearch } from './UserManagement';
import styled from 'styled-components';
import { Row, Select, Space } from 'antd';
import CustomButton from '../components/common/Button';
import { PlusCircleOutlined } from '@ant-design/icons';
import { CustomTag, StyledActionButton, StyledSpaceWrapper } from './Facility';
import { debounce } from '../utils/utils';
import { Drawer } from '../components/common/Drawer';
import EventEditForm from '../components/features/Events/EventEditForm';

type EventListNavigationState = {
  page: number;
  searchTerm: string;
  status?: EventStatus;
};

const eventStatusOptions = [
  { label: 'Draft', value: EventStatus.Draft },
  { label: 'Published', value: EventStatus.Open },
  { label: 'In Progress', value: EventStatus.InProgress },
  { label: 'Completed', value: EventStatus.Completed },
  { label: 'Cancelled', value: EventStatus.Cancelled },
];
const { Option } = Select;

const Events = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const pageSize = 8;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [eventStatusFilter, setEventStatusFilter] = useState<
    EventStatus | undefined
  >(undefined);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);

  const fetchEvents = useCallback(
    async (page = 1, searchTerm = '', status?: EventStatus) => {
      setLoading(true);
      try {
        const params: EventSearchParams = {
          pageNumber: Math.max(0, page - 1),
          pageSize: pageSize,
          searchTerm: searchTerm || undefined,
          status: status,
        };
        const data = await searchEventsRequest(params);
        if (data?.isSuccess) {
          setEvents(data.value?.rows || []);
          setTotalCount(data?.value?.totalCount || 0);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    },
    [pageSize],
  );

  const handleEditClick = (event: EventType) => {
    setSelectedEvent(event);
    setEditDrawerOpen(true);
  };

  const handleEditSuccess = () => {
    setEditDrawerOpen(false);
    setSelectedEvent(null);
    fetchEvents(currentPage, searchTerm, eventStatusFilter);
  };

  const handleEditCancel = () => {
    setEditDrawerOpen(false);
    setSelectedEvent(null);
  };

  const columns = [
    { title: 'Event Name', dataIndex: 'eventName', key: 'eventName' },
    {
      title: 'Sports',
      key: 'sports',
      render: (_: unknown, record: EventType) => {
        const limit = 2;
        const sportsToShow =
          record.sportNames.length > limit
            ? record.sportNames.slice(0, limit).join(', ') + ',...'
            : record.sportNames.join(', ');
        return <>{sportsToShow}</>;
      },
    },
    {
      title: 'Registration Date',
      key: 'registrationDates',
      render: (_: unknown, record: EventType) => (
        <>
          {convertUtcToLocal(
            record.registrationStartDate,
            DateFormatStrings.DD_MMM_YYYY,
          )}
          {' - '}
          {convertUtcToLocal(
            record.registrationEndDate,
            DateFormatStrings.DD_MMM_YYYY,
          )}
        </>
      ),
    },
    {
      title: 'Status',
      key: 'location',
      render: (_: unknown, record: EventType) => {
        const colorMap = {
          Draft: 'default',
          Open: 'processing',
          InProgress: 'warning',
          Completed: 'success',
          Cancelled: 'error',
        };
        const status =
          record.eventStatusName === 'Open'
            ? 'Published'
            : record.eventStatusName;

        return (
          <CustomTag
            color={colorMap[record.eventStatusName as keyof typeof colorMap]}
          >
            {status}
          </CustomTag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: EventType) => (
        <StyledSpaceWrapper>
          <StyledActionButton
            type="text"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(record);
            }}
            disabled={record.eventStatus === EventStatus.Completed}
          >
            Edit
          </StyledActionButton>
        </StyledSpaceWrapper>
      ),
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchEvents(page, searchTerm, eventStatusFilter);
  };

  const handleEventStatusChange = (value: unknown) => {
    setEventStatusFilter(value as EventStatus | undefined);
    setCurrentPage(1); // Reset to first page when filter changes
    fetchEvents(1, searchTerm, value as EventStatus | undefined);
  };

  const debouncedFetchFacilities = useMemo(
    () =>
      debounce((page, query, status) => {
        fetchEvents(page, query, status);
      }, 500),
    [fetchEvents],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    debouncedFetchFacilities(1, value, eventStatusFilter);
  };

  const handleSearchSubmit = () => {
    setCurrentPage(1);
    fetchEvents(1, searchTerm, eventStatusFilter);
  };

  const handleRowClick = (eventId: number) => {
    navigate(`${EVENTMANAGEMENTPATH}/${eventId}`, {
      state: {
        page: currentPage,
        searchTerm: searchTerm,
        status: eventStatusFilter,
      } satisfies EventListNavigationState,
    });
  };

  useEffect(() => {
    const state = location.state as EventListNavigationState | undefined;

    const restoredPage = state?.page ?? 1;
    const restoredSearch = state?.searchTerm ?? '';
    const restoredStatus = state?.status ?? undefined;

    if (state) {
      setCurrentPage(restoredPage);
      setSearchTerm(restoredSearch);
      setEventStatusFilter(restoredStatus);
      navigate(location.pathname, { replace: true, state: null });
    }

    fetchEvents(restoredPage, restoredSearch, restoredStatus);
  }, []);

  return (
    <Container>
      <Header>
        <StyledSpace direction="horizontal" size={24}>
          <StyledSearch
            placeholder="Search by Event Name, Sport..."
            onChange={handleSearchChange}
            onSearch={handleSearchSubmit}
            enterButton
            size="large"
            allowClear
            value={searchTerm}
            disabled={loading}
          />
          <StyledStatusSelect
            placeholder="Filter by Status"
            value={eventStatusFilter}
            onChange={handleEventStatusChange}
            allowClear
            size="large"
            disabled={loading}
          >
            {eventStatusOptions.map((option) => (
              <Option key={option.label} value={option.value}>
                {option.label}
              </Option>
            ))}
          </StyledStatusSelect>
          <CustomButton
            type="primary"
            icon={<PlusCircleOutlined style={{ fontSize: TEXT_SIZES.LG }} />}
            onClick={() => navigate(EVENTCREATIONPATH)}
            width="182px"
            disabled={loading}
          >
            Add New Event
          </CustomButton>
        </StyledSpace>
      </Header>
      <CustomTable
        dataSource={events}
        columns={columns}
        rowKey="eventId"
        loading={loading}
        onRowClick={(record) => {
          handleRowClick(record.eventId);
        }}
        pagination={{
          pageSize: pageSize,
          current: currentPage,
          total: totalCount,
          onChange: (page) => handlePageChange(page),
        }}
      />
      {editDrawerOpen && (
        <Drawer
          title="Edit Event"
          open={editDrawerOpen}
          onClose={handleEditCancel}
          width="600px"
        >
          <EventEditForm
            event={selectedEvent}
            onSuccess={handleEditSuccess}
            onCancel={handleEditCancel}
          />
        </Drawer>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  height: 100%;
`;

const Header = styled(Row)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;
const StyledSpace = styled(Space)`
  width: 100%;
  & .ant-space-item:first-child {
    flex: 1;
  }
`;

const StyledStatusSelect = styled(Select)`
  width: 200px;
  .ant-select-selector {
    height: 40px !important;
    display: flex;
    align-items: center;
  }
`;

export default Events;
